import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import config from '../../../../config';
import MainLayout from '../../../../components/MainLayout';
import FinishedLectureList from '../../components/class/desktop/components/FinishedLectureList';
import { getTestsToPrint } from '../../../../redux/api/question';
import { registerPdfDownload, registerVideoPlay } from '../../../../redux/api/class';
import VideoPlayer from '../../../../components/VideoPlayer.component';
import { checkPodcastEpisodePermission } from '../../../../helpers/podcast';
import { allowedFeature } from '../../../../helpers/permissions';
import { isLessonLocked } from '../../../../utils/lessonLock';
import { useQuestionBlocksToggle } from '../../hooks/useQuestionBlocksToggle';
import Player from '../../../../components/Podcast/Player';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { actions as podcastActions } from '../../../../redux/modules/podcast';
import * as S from './styles';
import spin from '../../../../img/svg/coursesIcon/spin.svg';
import check from '../../../../img/svg/coursesIcon/check.svg';
import lock from '../../../../img/svg/coursesIcon/lock.svg';
import infoIcon from '../../../../img/svg/coursesIcon/informations.svg';
import infoIconDisabled from '../../../../img/svg/coursesIcon/informations-disabled.svg';
import book from '../../../../img/svg/coursesIcon/book.svg';
import bookDisabled from '../../../../img/svg/coursesIcon/book-disabled.svg';
import test from '../../../../img/svg/coursesIcon/tests.svg';
import testDisabled from '../../../../img/svg/coursesIcon/tests-disabled.svg';
import audio from '../../../../img/svg/coursesIcon/audio.svg';
import audioDisabled from '../../../../img/svg/coursesIcon/audio-disabled.svg';
import up from '../../../../img/svg/coursesIcon/up.svg';
import down from '../../../../img/svg/coursesIcon/down-blue.svg';
import arrowLeft from '../../../../img/svg/coursesIcon/arrow-left.svg';
import download from '../../../../img/svg/coursesIcon/download.svg';
import { CourseDetail, ClassItem, CourseModule } from '../../../../types/courseModules';
import { CourseData, Lesson, LessonStatus, Module, Video } from '../../../../types/courseData';
import { formatExpirationDate } from '../../../../utils/dateFormatters';
import {
  countClassesInTree,
  findFirstAvailableClass,
  getModuleProgress,
  indexOfClassInTree,
} from '../../../../utils/moduleTree';

interface ExpandedModulesState {
  [key: number]: boolean;
}

// Persistência (por curso) do estado de expandido/recolhido dos módulos, para
// preservá-lo ao navegar para outra tela (ex.: prova) e voltar. sessionStorage
// limita o escopo à aba atual, igual ao já usado para completedTestClassId.
const expandedModulesStorageKey = (courseId: string) => `courseModulesExpanded:${courseId}`;

const readStoredExpandedModules = (courseId: string): ExpandedModulesState | null => {
  try {
    const raw = sessionStorage.getItem(expandedModulesStorageKey(courseId));
    return raw ? (JSON.parse(raw) as ExpandedModulesState) : null;
  } catch {
    return null;
  }
};

interface CourseDetailModulesProps {
  courseData: CourseData | null;
}

interface RouteParams {
  courseId: string;
  courseClassId: string;
}

interface ReduxState {
  podcast: {
    availableClassesEpisodes: any;
    currentPlay: any;
    lastCurrentTimeTrack: any;
    currentPodcast: any;
  };
  auth: {
    user: {
      id_profile: number;
    };
  };
}

const CourseDetailModules: React.FC<CourseDetailModulesProps> = ({ courseData }) => {
  const { courseId, courseClassId } = useParams<RouteParams>();
  const history = useHistory();
  const dispatch = useDispatch();

  const availableClassesEpisodes = useSelector(
    (state: ReduxState) => state.podcast.availableClassesEpisodes,
  );
  const currentPlay = useSelector((state: ReduxState) => state.podcast.currentPlay);
  const lastCurrentTimeTrack = useSelector(
    (state: ReduxState) => state.podcast.lastCurrentTimeTrack,
  );
  const currentPodcast = useSelector((state: ReduxState) => state.podcast.currentPodcast);
  const userId = useSelector((state: ReduxState) => state.auth.user?.id_profile);

  const formattedExpirationDate = useMemo(
    () => formatExpirationDate(courseData?.report?.[0]?.data_fim),
    [courseData],
  );

  const [selectedClass, setSelectedClass] = useState<ClassItem | null>(null);
  // null = nenhum estado salvo; o efeito abaixo expande o caminho padrão.
  const [expandedModules, setExpandedModules] = useState<ExpandedModulesState | null>(() =>
    readStoredExpandedModules(courseId),
  );
  const [activeTab, setActiveTab] = useState<string>('info');
  const [showPodcast, setShowPodcast] = useState<boolean>(false);
  const [foundPodcast, setFoundPodcast] = useState<boolean>(false);
  const [loadingDownloadText, setLoadingDownloadText] = useState<boolean>(false);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [localCompletions, setLocalCompletions] = useState<{
    [classId: number]: { pdfDownloaded?: boolean; videoPlayed?: boolean; testCompleted?: boolean };
  }>({});

  const { hasQuestionBlocks, useQuestionBlocks, handleToggleQuestionBlocks } =
    useQuestionBlocksToggle(selectedClass?.id);

  const courseReport = useMemo(() => courseData?.report?.[0] || null, [courseData]);

  const course = useMemo(() => {
    if (!courseData) return null;

    const calculateLessonStatus = (
      lesson: Lesson,
      classId: number,
    ): 'completed' | 'in_progress' | 'none' => {
      const steps: { required: boolean; completed: boolean }[] = [];
      const localCompletion = localCompletions[classId] || {};

      // Etapa 1: PDF (só conta se has_pdf for true)
      if (lesson.has_pdf) {
        steps.push({
          required: true,
          completed: localCompletion.pdfDownloaded || lesson.downloaded_pdf || false,
        });
      }

      // Etapa 2: Vídeo (só conta se tiver vídeos)
      if (lesson.aula_videos && lesson.aula_videos.length > 0) {
        steps.push({
          required: true,
          completed: localCompletion.videoPlayed || lesson.video_played > 0,
        });
      }

      // Etapa 3: Prova (só conta se have_lecture for true)
      if (lesson.have_lecture) {
        steps.push({
          required: true,
          completed: localCompletion.testCompleted || lesson.first_test || false,
        });
      }

      // Se não há etapas aplicáveis, retorna 'none'
      if (steps.length === 0) return 'none';

      // Verifica quantas etapas foram completadas
      const completedSteps = steps.filter((step) => step.completed).length;
      const totalSteps = steps.length;

      // Todas as etapas completadas = completed
      if (completedSteps === totalSteps) return 'completed';

      // Pelo menos uma etapa completada = in_progress
      if (completedSteps > 0) return 'in_progress';

      // Nenhuma etapa completada = none
      return 'none';
    };

    // Obtém as classes do report para usar como fonte de dados
    const reportClasses = courseData.report?.[0]?.classes || [];

    type PendingClass = ClassItem & {
      baseStatus: 'completed' | 'in_progress' | 'none';
      apiStatus: LessonStatus;
      apiPrincipal: boolean;
    };

    interface PendingModule {
      id: number;
      title: string;
      expanded: boolean;
      depth: number;
      classes: PendingClass[];
      children: PendingModule[];
    }

    // Primeiro, mapeia os módulos (recursivamente, incluindo submódulos) e
    // aulas SEM calcular locked ainda. Cursos de um nível só chegam sem
    // `children` e seguem o mesmo caminho, com a árvore degenerando em lista.
    const mapModuleNode = (mod: Module, depth: number): PendingModule => ({
      id: mod.id,
      title: mod.title,
      expanded: false,
      depth,
      classes:
        mod.lessons?.map((lesson) => {
          const reportClass = reportClasses.find(
            (reportClass) => reportClass.id === lesson.id_aula,
          );

          const classData = reportClass || lesson;
          const classId = classData.id || lesson.id_aula;
          const baseStatus = calculateLessonStatus(classData, classId);

          return {
            id: classData.id || lesson.id_aula,
            title: classData.titulo || lesson.titulo,
            type: (classData.simulado ? 'exam' : classData.have_lecture ? 'video' : 'text') as
              | 'video'
              | 'text'
              | 'exam',
            duration: classData.class_time || undefined,
            status: baseStatus as 'completed' | 'in_progress' | 'locked' | 'none',
            baseStatus,
            apiStatus: classData.status,
            apiPrincipal: classData.principal,
            videoUrl: classData.aula_videos?.[0]?.video_embed || null,
            description: classData.texto || '',
            hasPdf: classData.has_pdf || false,
            materials: classData.pdf_file
              ? [{ name: 'Material da aula', url: classData.pdf_file }]
              : [],
            audio: classData.audio_embed || null,
            hasTest: classData.have_lecture || false,
            firstTest: classData.first_test || false,
            secondTest: classData.second_test || false,
            days: classData.days || [],
          };
        }) || [],
      children: (mod.children || []).map((child) => mapModuleNode(child, depth + 1)),
    });

    // A profundidade é sempre calculada localmente a partir da estrutura de
    // `children`, ignorando o `depth` que a API possa enviar, para garantir
    // que a indentação reflita exatamente a árvore renderizada.
    const modulesWithClasses = courseData.modules?.map((mod) => mapModuleNode(mod, 0)) || [];

    // A regra do cadeado vive inteira em src/utils/lessonLock.ts: o bloqueio vem
    // do `status` da API, nunca da data em `days` -- com a configuracao
    // "Desbloquear aula" uma aula pode exibir data futura e estar liberada.
    const resolveModule = (module: PendingModule): CourseModule => ({
      ...module,
      classes: module.classes.map(({ baseStatus, apiStatus, apiPrincipal, ...rest }) => {
        const status: ClassItem['status'] = isLessonLocked({
          status: apiStatus,
          principal: apiPrincipal,
        })
          ? 'locked'
          : baseStatus;

        return { ...rest, status };
      }),
      children: module.children.map(resolveModule),
    });

    const resolvedModules = modulesWithClasses.map(resolveModule);

    const mappedCourse: CourseDetail = {
      id: courseData.report?.[0]?.id || 0,
      title: courseData.report?.[0]?.title || 'Curso',
      category: courseData.report?.[0]?.course_category || 'Categoria',
      description: courseData.report?.[0]?.description || courseData.description || '',
      daysRemaining: courseData.report?.[0]?.expiration_date || courseData.expiration_date || 0,
      modules: resolvedModules,
    };

    return mappedCourse;
  }, [courseData, localCompletions]);

  useEffect(() => {
    if (course && !selectedClass) {
      // Expande o caminho inteiro (ancestrais + módulo dono) até a primeira
      // aula disponível, para que ela fique visível mesmo dentro de submódulos.
      const { classItem, modulePathIds } = findFirstAvailableClass(course.modules);

      // Um estado salvo (navegou e voltou) tem prioridade sobre a expansão padrão.
      if (!expandedModules) {
        const expandedState: ExpandedModulesState = {};
        if (modulePathIds.length > 0) {
          modulePathIds.forEach((id) => {
            expandedState[id] = true;
          });
        } else if (course.modules[0]) {
          expandedState[course.modules[0].id] = true;
        }
        setExpandedModules(expandedState);
      }

      if (classItem) {
        setSelectedClass(classItem);
      }
    }
    // eslint-disable-next-line
  }, [course, selectedClass]);

  useEffect(() => {
    if (!expandedModules) return;
    try {
      sessionStorage.setItem(expandedModulesStorageKey(courseId), JSON.stringify(expandedModules));
    } catch {
      // storage indisponível: segue só com o estado em memória.
    }
  }, [expandedModules, courseId]);

  const toggleModule = (moduleId: number) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev?.[moduleId],
    }));
  };

  const handleClassClick = (classItem: ClassItem) => {
    if (classItem.status !== 'locked') {
      setSelectedClass(classItem);
    }
  };

  const handleOpenClassPodcast = () => {
    if (!selectedClass?.id || !courseId || !courseData) return;

    const codClass = courseData.report?.[0]?.classes?.find(
      (item) => item.id === selectedClass.id,
    )?.cod_aula;

    setShowPodcast(true);
    dispatch(
      podcastActions.fetchByClass(Number(courseId), String(codClass), () => {
        dispatch(podcastActions.closePlayerFooter());
      }),
    );
  };

  const handleLeaveEpisodePodcast = () => {
    const currentTime = currentPlay?.currentTime;
    const duration = currentPlay?.duration;

    if (!duration || !currentTime) {
      setShowPodcast(false);
      return;
    }

    const percentFinish = Math.floor((currentTime / duration) * 100);
    const closePodcast = () => setShowPodcast(false);

    dispatch(
      podcastActions.track(
        {
          ...currentPodcast,
          finished: percentFinish > 90,
          currentTime,
          lastCurrentTimeTrack,
        },
        closePodcast,
        closePodcast,
      ),
    );
  };

  const handleReview = (lectureId: number, idAula: number) => {
    history.push(`/lectures/${courseId}/${courseClassId}/${lectureId}`, {
      selectedClass,
      course: courseReport,
      idAula,
    });
  };

  const handlePrintTest = async () => {
    if (!selectedClass?.id) return;

    setLoadingDownloadText(true);
    try {
      const { filename } = await getTestsToPrint(selectedClass.id);

      if (filename && filename !== '') {
        window.open(filename, '_blank');
      }
    } catch (error) {
      console.error('Erro ao baixar prova:', error);
    } finally {
      setLoadingDownloadText(false);
    }
  };

  const handleMaterialDownload = async (materialUrl: string) => {
    if (!selectedClass?.id || !userId || !courseId || !courseClassId) return;

    const courseTag = courseData?.report?.[0]?.tag || course?.category || '';

    try {
      await registerPdfDownload(userId, courseTag, selectedClass.id, courseClassId);

      setLocalCompletions((prev) => ({
        ...prev,
        [selectedClass.id]: {
          ...prev[selectedClass.id],
          pdfDownloaded: true,
        },
      }));

      window.open(`${config.s3Url}${materialUrl}`, '_blank');
    } catch (error) {
      console.error('Erro ao registrar download do material:', error);
      window.open(`${config.s3Url}${materialUrl}`, '_blank');
    }
  };

  const handlePlayVideo = useCallback(
    (video: Video) => {
      if (!video || !selectedClass?.id || !userId || !courseId || !courseClassId) return;

      const courseTag = courseData?.report?.[0]?.tag || course?.category || '';

      registerVideoPlay(userId, courseTag, selectedClass.id, video.id_video, courseClassId).then(
        (payload: { video_played?: number }) => {
          const video_played = payload?.video_played ?? 0;
          if (video_played > 0) {
            setLocalCompletions((prev) => ({
              ...prev,
              [selectedClass.id]: {
                ...prev[selectedClass.id],
                videoPlayed: true,
              },
            }));
          }
        },
      );
    },
    [selectedClass, userId, courseId, courseClassId, courseData, course],
  );

  useEffect(() => {
    const completedTestClassId = sessionStorage.getItem('completedTestClassId');
    if (completedTestClassId) {
      const classId = parseInt(completedTestClassId, 10);
      setLocalCompletions((prev) => ({
        ...prev,
        [classId]: {
          ...prev[classId],
          testCompleted: true,
        },
      }));
      sessionStorage.removeItem('completedTestClassId');
    }
  }, []);

  useEffect(() => {
    setShowPodcast(false);

    if (!courseData) return;

    const codClass = courseData.report?.[0]?.classes?.find(
      (item) => item.id === selectedClass?.id,
    )?.cod_aula;

    if (selectedClass?.id && availableClassesEpisodes) {
      const allowed = checkPodcastEpisodePermission(availableClassesEpisodes, String(codClass));
      setFoundPodcast(allowed);
    } else {
      setFoundPodcast(false);
    }

    if (selectedClass) {
      const reportClass = courseData.report?.[0]?.classes?.find((c) => c.id === selectedClass.id);
      const videos = reportClass?.aula_videos || [];
      const firstVideo = videos.find((v) => v.video_embed);
      setCurrentVideo(firstVideo || null);
    }

    //eslint-disable-next-line
  }, [selectedClass?.id, availableClassesEpisodes, courseData]);

  const contQuantityClassesOnModules = useMemo(() => {
    if (!course || !selectedClass) return null;
    return countClassesInTree(course.modules);
    // eslint-disable-next-line
  }, [course?.modules, selectedClass]);

  const indexCurrentClassOnModules = useMemo(() => {
    if (!course) return 0;
    return indexOfClassInTree(course.modules, selectedClass?.id);
    // eslint-disable-next-line
  }, [course?.modules, selectedClass?.id]);

  // Renderiza um nó da árvore de módulos: cabeçalho, aulas próprias e, abaixo,
  // os submódulos (recursivamente). A indentação cresce com `depth` para
  // deixar claro o nível de cada módulo; cursos de um nível só (depth 0, sem
  // children) ficam visualmente idênticos ao layout anterior.
  const renderModuleNode = (module: CourseModule, isLast: boolean): React.ReactNode => {
    const isOpen = !!expandedModules?.[module.id];
    const hasContent = module.classes.length > 0 || module.children.length > 0;
    const progress = getModuleProgress(module);

    return (
      <div key={module.id}>
        <S.ModuleHeader
          isOpen={isOpen}
          depth={module.depth}
          onClick={() => toggleModule(module.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleModule(module.id);
            }
          }}
          isLast={isLast && !isOpen}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
        >
          <span className="module-title">{module.title}</span>
          <S.ModuleHeaderActions>
            {progress.total > 0 && (
              <S.ModuleProgress isOpen={isOpen}>
                <span className="module-progress-text">
                  {progress.completed}/{progress.total} aulas · {progress.percent}%
                </span>
                <div
                  className="module-progress-bar"
                  role="progressbar"
                  aria-label={`Progresso do módulo ${module.title}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress.percent}
                >
                  <div className="module-progress-fill" style={{ width: `${progress.percent}%` }} />
                </div>
              </S.ModuleProgress>
            )}
            {isOpen ? <img src={up} alt="up" /> : <img src={down} alt="down" />}
          </S.ModuleHeaderActions>
        </S.ModuleHeader>

        {isOpen && (
          <div>
            {!hasContent && (
              <S.EmptyModuleMessage depth={module.depth}>
                Este módulo ainda não possui aulas.
              </S.EmptyModuleMessage>
            )}

            {module.classes.map((lesson) => (
              <S.ClassItem
                key={lesson.id}
                depth={module.depth}
                isActive={selectedClass?.id === lesson.id}
                locked={lesson.status === 'locked'}
                onClick={() => handleClassClick(lesson)}
              >
                <span className="class-title">{lesson.title}</span>
                <div className="status-icon">
                  {lesson.status === 'completed' ? (
                    <img src={check} alt="completed" width={24} height={24} />
                  ) : lesson.status === 'in_progress' ? (
                    <img src={spin} alt="in_progress" width={24} height={24} />
                  ) : lesson.status === 'locked' ? (
                    <img src={lock} alt="locked" width={24} height={24} />
                  ) : (
                    <img src={infoIconDisabled} alt="no_content" width={24} height={24} />
                  )}
                </div>
              </S.ClassItem>
            ))}

            {module.children.map((child) =>
              renderModuleNode(
                child,
                isLast && module.children[module.children.length - 1] === child,
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  if (!courseData || !courseId || !courseClassId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Dados insuficientes para exibir o conteúdo.</p>
      </div>
    );
  }

  if (!course) {
    return (
      <MainLayout>
        <S.LoadingContainer>Curso não encontrado</S.LoadingContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={[{ label: 'Meus cursos' }, { label: course.title }]} course={course}>
      <S.PageContainer>
        <S.BreadcrumbRow>
          <div>
            <span className="link">Cursos</span>
            <span className="separator">/</span>
            <span className="text">
              {indexCurrentClassOnModules + 1} de {contQuantityClassesOnModules}
            </span>
          </div>
          {formattedExpirationDate && (
            <span className="expiry-tag">Disponível até {formattedExpirationDate}</span>
          )}
        </S.BreadcrumbRow>

        <S.MainCard>
          <S.LeftColumn>
            <S.LessonHeader>
              <h1>{selectedClass?.title || course.title}</h1>
              <div className="lesson-badge">Aula {indexCurrentClassOnModules + 1}</div>
            </S.LessonHeader>

            <S.ContentSection>
              <S.TabsHeader role="tablist" aria-label="Conteúdo da aula">
                <S.TabItem
                  isActive={activeTab === 'info'}
                  onClick={() => setActiveTab('info')}
                  bgIconImage={infoIcon}
                  bgIconNoActive={infoIconDisabled}
                  role="tab"
                  aria-selected={activeTab === 'info'}
                  aria-controls="info-panel"
                  id="info-tab"
                >
                  <div className="icon" />
                  <span>Informações</span>
                </S.TabItem>

                <S.TabItem
                  isActive={activeTab === 'material'}
                  onClick={() => setActiveTab('material')}
                  bgIconImage={book}
                  bgIconNoActive={bookDisabled}
                  role="tab"
                  aria-selected={activeTab === 'material'}
                  aria-controls="material-panel"
                  id="material-tab"
                >
                  <div className="icon" />
                  <span>Material</span>
                </S.TabItem>

                <S.TabItem
                  isActive={activeTab === 'audio'}
                  onClick={() => setActiveTab('audio')}
                  bgIconImage={audio}
                  bgIconNoActive={audioDisabled}
                  role="tab"
                  aria-selected={activeTab === 'audio'}
                  aria-controls="audio-panel"
                  id="audio-tab"
                >
                  <div className="icon" />
                  <span>Áudio</span>
                </S.TabItem>
                <S.TabItem
                  isActive={activeTab === 'quiz'}
                  onClick={() => setActiveTab('quiz')}
                  bgIconImage={test}
                  bgIconNoActive={testDisabled}
                  role="tab"
                  aria-selected={activeTab === 'quiz'}
                  aria-controls="quiz-panel"
                  id="quiz-tab"
                >
                  <div className="icon" />
                  <span>Prova</span>
                </S.TabItem>
              </S.TabsHeader>

              <S.TabContent
                role="tabpanel"
                id={`${activeTab}-panel`}
                aria-labelledby={`${activeTab}-tab`}
              >
                {activeTab === 'info' && (
                  <S.DescriptionText
                    dangerouslySetInnerHTML={{
                      __html: selectedClass?.description || course.description || 'Bem-vindo!',
                    }}
                  />
                )}

                {activeTab === 'material' && (
                  <>
                    {selectedClass?.hasPdf &&
                    selectedClass?.materials &&
                    selectedClass.materials.length > 0 ? (
                      selectedClass.materials.map((material, index) => (
                        <S.ButtonSectionsArea
                          key={index}
                          onClick={() => handleMaterialDownload(material.url)}
                        >
                          <img src={book} alt="book" />
                          <span>{material.name}</span>
                          <img src={arrowLeft} alt="arrow" />
                        </S.ButtonSectionsArea>
                      ))
                    ) : (
                      <S.ButtonSectionsArea disabled>
                        <img src={bookDisabled} alt="book" />
                        <span>Aula sem material disponível</span>
                      </S.ButtonSectionsArea>
                    )}
                  </>
                )}

                {activeTab === 'audio' && (
                  <>
                    {showPodcast ? (
                      <>
                        <div style={{ marginBottom: '20px' }}>
                          <S.ButtonSectionsArea onClick={handleLeaveEpisodePodcast}>
                            <ArrowLeftOutlined style={{ fontSize: '20px' }} />
                            <span>Voltar</span>
                          </S.ButtonSectionsArea>
                        </div>
                        <div
                          style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px' }}
                        >
                          <h3 style={{ marginBottom: '20px' }}>Ouça o Podcast</h3>
                          <Player />
                        </div>
                      </>
                    ) : allowedFeature('podcast') && foundPodcast ? (
                      <S.ButtonSectionsArea onClick={handleOpenClassPodcast}>
                        <img src={audio} alt="audio" />
                        <span>Ouça o áudio do dia</span>
                        <img src={arrowLeft} alt="arrow" />
                      </S.ButtonSectionsArea>
                    ) : (
                      // sem podcast
                      <S.ButtonSectionsArea disabled>
                        <img src={audioDisabled} alt="audio" />
                        <span>Aula sem áudio disponível</span>
                      </S.ButtonSectionsArea>
                    )}
                  </>
                )}

                {activeTab === 'quiz' && (
                  <>
                    {selectedClass?.hasTest ? (
                      <>
                        {hasQuestionBlocks && (
                          <div style={{ marginBottom: '12px' }}>
                            <Checkbox
                              checked={useQuestionBlocks}
                              onChange={(event) =>
                                handleToggleQuestionBlocks(event.target.checked)
                              }
                            >
                              Dividir questões em blocos
                            </Checkbox>
                          </div>
                        )}
                        <S.ButtonSectionsArea
                          onClick={() => {
                            history.push(`/lectures/${courseId}/${courseClassId}`, {
                              selectedClass,
                              course: courseReport,
                            });
                          }}
                        >
                          <img src={test} alt="test" />
                          <span>Iniciar prova</span>
                          <img src={arrowLeft} alt="arrow" />
                        </S.ButtonSectionsArea>

                        <div style={{ marginTop: '16px', marginBottom: '20px' }}>
                          <S.ButtonSectionsArea
                            onClick={handlePrintTest}
                            disabled={loadingDownloadText}
                          >
                            <img src={download} alt="test" />
                            {loadingDownloadText ? (
                              <span>Imprimindo...</span>
                            ) : (
                              <span>Imprimir prova</span>
                            )}
                            <img src={arrowLeft} alt="arrow" />
                          </S.ButtonSectionsArea>
                        </div>

                        <div style={{ marginTop: '20px' }}>
                          <FinishedLectureList
                            classId={String(selectedClass.id)}
                            handleReview={handleReview}
                          />
                        </div>
                      </>
                    ) : (
                      <S.DescriptionText>Está aula não tem provas ou simulados.</S.DescriptionText>
                    )}
                  </>
                )}
              </S.TabContent>
            </S.ContentSection>

            {(() => {
              const reportClass = courseData?.report?.[0]?.classes?.find(
                (c) => c.id === selectedClass?.id,
              );
              const videos = reportClass?.aula_videos || [];
              const hasVideos = videos.some((v) => v.video_embed);

              if (!hasVideos) {
                return <p>Aula sem vídeo</p>;
              }

              return (
                <S.VideoContainer>
                  {videos.length > 1 ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          marginBottom: '16px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {videos.map((video, index) =>
                          video.video_embed ? (
                            <button
                              key={video.id_video}
                              onClick={() => setCurrentVideo(video)}
                              style={{
                                padding: '10px 20px',
                                backgroundColor:
                                  currentVideo?.id_video === video.id_video ? '#5768FF' : '#f0f0f0',
                                color: currentVideo?.id_video === video.id_video ? 'white' : '#333',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 500,
                              }}
                            >
                              Vídeo {index + 1}
                            </button>
                          ) : null,
                        )}
                      </div>
                      {currentVideo?.video_embed && (
                        <div style={{ width: '100%' }}>
                          <VideoPlayer
                            key={currentVideo.id_video}
                            videoEmbed={currentVideo.video_embed}
                            onPlay={() => handlePlayVideo(currentVideo)}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    videos.map((video) =>
                      video.video_embed ? (
                        <div style={{ width: '100%' }} key={video.id_video}>
                          <VideoPlayer
                            key={video.id_video}
                            videoEmbed={video.video_embed}
                            onPlay={() => handlePlayVideo(video)}
                          />
                        </div>
                      ) : null,
                    )
                  )}
                </S.VideoContainer>
              );
            })()}
          </S.LeftColumn>

          <S.RightColumn>
            <S.ModuleListContainer>
              {course.modules.map((module) =>
                renderModuleNode(module, course.modules[course.modules.length - 1] === module),
              )}
            </S.ModuleListContainer>
          </S.RightColumn>
        </S.MainCard>
      </S.PageContainer>
    </MainLayout>
  );
};

export default CourseDetailModules;
